import {
    Middleware,
    RequestContext,
    ResponseContext
} from 'hotdesk_typescript';
import * as Sentry from '@sentry/nextjs';
import {
    DEVICE_ID_HEADER,
    getDeviceId
} from '@/hotdesk-ui/utils/device-id';

const isDebug =
    process.env.NODE_ENV !== 'production' ||
    process.env.DEBUG_API === 'true' ||
    process.env.NEXT_PUBLIC_DEBUG_API === 'true';

const sanitizeHeaders = (headers: { [k: string]: string }) => {
    const out: { [k: string]: string } = { ...headers };
    if (out.Authorization) out.Authorization = '[redacted]';
    if (out.Cookie) out.Cookie = '[redacted]';
    return out;
};

const truncate = (text: string, max = 500) =>
    text.length > max ? text.slice(0, max) + '…' : text;

export class UpstreamApiError extends Error {
    public readonly upstream: {
        url?: string;
        method?: string;
        status: number;
        contentType: string;
        bodyPreview: string;
    };
    constructor(message: string, upstream: UpstreamApiError['upstream']) {
        super(message);
        this.name = 'UpstreamApiError';
        this.upstream = upstream;
    }
}

export const myPromiseMiddleware = (languageCode: string): Middleware => ({
    pre(context: RequestContext): Promise<RequestContext> {
        context.setQueryParam('lang', languageCode);
        // The provider (IWG/Regus) endpoints localise off the header rather
        // than the `lang` query param. Both carry the same value, so sending
        // them together keeps one language source for every route.
        context.setHeaderParam('Accept-Language', languageCode);

        const url = context.getUrl();
        const method = context.getHttpMethod().toString();

        // The booking quote/create/release trio carries the per-browser device
        // id so the BE can correlate quotes minted anonymously with the create
        // (or release) that follows after login. Scoped to those three routes
        // so bookingList/bookingRead GETs and bookingDelete stay untouched.
        // getDeviceId() is null during SSR, where no device identity exists.
        // Requires `x-device-id` in the BE CORS allow-list.
        const isQuoteLifecycleRoute =
            (method === 'POST' && /\/booking\/(quote\/)?(\?|$)/.test(url)) ||
            (method === 'DELETE' && /\/booking\/quote\/(\?|$)/.test(url));
        if (isQuoteLifecycleRoute) {
            const deviceId = getDeviceId();
            if (deviceId) {
                context.setHeaderParam(DEVICE_ID_HEADER, deviceId);
            }
        }

        Sentry.addBreadcrumb({
            category: 'api.request',
            type: 'http',
            level: 'info',
            data: { url, method }
        });

        if (isDebug) {
            try {
                console.log(`[API →] ${method} ${url}`, {
                    headers: sanitizeHeaders(context.getHeaders()),
                    body: context.getBody()
                });
            } catch {
                // ignore logging failures
            }
        }
        return Promise.resolve(context);
    },

    async post(context: ResponseContext): Promise<ResponseContext> {
        const status = context.httpStatusCode;
        const contentType = context.headers['content-type'] || '';
        const url = context.headers['x-internal-request-url'];
        const method = context.headers['x-internal-request-method'];

        let text = '';
        try {
            text = await context.body.text();
        } catch {
            // If body read fails, fall through with original context — don't mask the underlying error.
            return context;
        }

        // Re-wrap body so the SDK can still consume it downstream.
        context.body = {
            text: async () => text,
            binary: async () => new Blob([text])
        };

        const looksHtml = text.trimStart().startsWith('<');

        Sentry.addBreadcrumb({
            category: 'api.response',
            type: 'http',
            level: status >= 400 || looksHtml ? 'error' : 'info',
            data: {
                url,
                method,
                status,
                contentType,
                bodyPreview: truncate(text, 200)
            }
        });

        if (isDebug) {
            const logger = status >= 400 || looksHtml ? console.error : console.log;
            logger(`[API ←] ${status} ${method} ${url}`, {
                contentType,
                body: truncate(text, 1000)
            });
        }

        // HTML where JSON was expected = upstream misroute / WAF block / wrong host.
        // Throw with full context so Sentry sees the URL, status, content-type, and body preview
        // instead of a downstream `JSON.parse` SyntaxError with no breadcrumbs.
        if (looksHtml) {
            const err = new UpstreamApiError(
                `Upstream API returned HTML instead of JSON (status=${status}, url=${url})`,
                {
                    url,
                    method,
                    status,
                    contentType,
                    bodyPreview: truncate(text, 500)
                }
            );
            Sentry.withScope((scope) => {
                scope.setContext('upstream', err.upstream);
                scope.setTag('upstream.status', String(status));
                scope.setTag('upstream.contentType', contentType || 'unknown');
                Sentry.captureException(err);
            });
            throw err;
        }

        return context;
    }
});
